// L'accueil — passage obligé à chaque ouverture, accueil et briefing fusionnés.
// Six blocs, pas davantage : au-delà il devient un tableau de bord qu'on
// traverse sans lire (§6.1).
//
// Deux règles tiennent cet écran :
//   • ENTRER DANS LA RUN est l'action de la page — une seule `pri`, la plus
//     grande, la plus proche du bloc qu'on vient de lire ;
//   • tout le reste est INFORMATIF, et le « coup d'œil sur hier » contient des
//     ÉCARTS : ils passent par @/domain/ecart, jamais par un fmtEur nu qui
//     ferait lire un excédent de 50 € comme un montant ordinaire.

import { useCallback, useMemo, useState } from 'react'
import { addDays, fmtEur, formatDate, formatMedium, todayIso } from 'sotcp-core/dayUtils'
import { useAccueil } from '@/data/useAccueil'
import * as api from '@/data/api'
import { ceQuiAttend, resteDHier } from '@/domain/briefing'
import { etatCoffre } from '@/domain/derive'
import { EPSILON, couleurEcart, fmtEcart, lampeEcart, motEcart, severite } from '@/domain/ecart'
import { progression, etatVerrou } from '@/domain/parcours'
import { duree, heure, initiales, pluriel, timeAgo } from '@/lib/format'
import { messageErreur } from '@/lib/errors'
import { aller } from '@/lib/router'
import { useAuth } from '@/lib/auth'
import { Card, Chip, Kpis, Lamp, Modale, Notice, Vide } from '@/ui/primitives'
import { useToast } from '@/ui/Toast'
import type { Etape, Rapport } from '@/domain/types'
import type { Couleur } from '@/ui/primitives'

export default function Accueil() {
  const aujourdhui = todayIso()
  const [selection, setSelection] = useState(aujourdhui)
  const d = useAccueil(selection)
  const { user } = useAuth()
  const toast = useToast()
  const [entree, setEntree] = useState(false)
  const [demandeMain, setDemandeMain] = useState(false)

  const jours = useMemo(() => {
    const connues = new Map(d.journees.map((j) => [j.date, j.status]))
    return Array.from({ length: 8 }, (_, i) => addDays(aujourdhui, -i))
      .map((date) => ({ date, status: connues.get(date) ?? null }))
  }, [d.journees, aujourdhui])

  // Le chargement ne remet pas `chargement` à true quand on change de jour : les
  // étapes, la journée et « hier » restent alors ceux du jour PRÉCÉDEMMENT
  // sélectionné. Le seul signal fiable est la présence de la clé du jour dans
  // `etapes`, que le chargement réécrit en entier. Sans cette garde on annonce
  // « run non commencée » sur une run en cours, et « journée soldée » sur une
  // journée dont on n'a encore rien lu.
  const aJour = Object.prototype.hasOwnProperty.call(d.etapes, selection)
  const pret = !d.chargement && aJour

  const etapesSel: Etape[] = d.etapes[selection] ?? []
  const run = d.runs[selection]
  const verrou = etatVerrou(run ?? null, user?.id ?? null)
  const p = progression(etapesSel)

  // Reprendre, c'est reprendre là où on s'est arrêté : une étape ouverte prime
  // sur la première étape à faire, sinon le bouton renvoie en arrière du travail
  // déjà commencé.
  const courante = etapesSel.find((e) => e.statut === 'en_cours')
    ?? etapesSel.find((e) => e.statut === 'a_faire')
    ?? etapesSel.find((e) => e.statut === 'de_cote')

  const rapportPrec = d.journeePrec?.rapport ?? null

  const reste = useMemo(() => {
    const l = resteDHier({
      datePrec: d.datePrec, rapportPrec,
      etapesPrec: d.datePrec ? (d.etapes[d.datePrec] ?? []) : [],
      pad: d.pad, consignes: d.consignes, aujourdhui,
    })
    // Le plus grave en tête : la liste est tronquée à douze lignes, on ne peut
    // pas se permettre de couper un « stop » pour garder un « warn ».
    return [...l].sort((a, b) => (a.gravite === b.gravite ? 0 : a.gravite === 'stop' ? -1 : 1))
  }, [d.datePrec, d.etapes, d.pad, d.consignes, rapportPrec, aujourdhui])

  const graviteReste: Couleur = !pret ? 'neutre'
    : reste.some((r) => r.gravite === 'stop') ? 'stop'
    : reste.length > 0 ? 'warn' : 'ok'

  const attendu = useMemo(() => ceQuiAttend({
    date: selection, rapport: d.journeeSel?.rapport ?? null,
    journeesOuvertes: d.journees.filter((j) => j.status === 'active').map((j) => j.date),
  }), [d.journeeSel, d.journees, selection])

  const hier = useMemo(() => bilanHier(rapportPrec), [rapportPrec])

  const entrer = useCallback(async (force: boolean) => {
    if (entree) return                    // le clic double ouvrirait deux fois la run
    setEntree(true)
    try {
      await api.ouvrirRun(selection)
      await api.prendreMain(selection, force)
      setDemandeMain(false)
      aller({ vue: 'run', date: selection, etape: null })
      // Volontairement pas de setEntree(false) : on quitte l'écran. Le remettre
      // à false écrirait dans un composant démonté et rouvrirait le bouton le
      // temps d'une image.
    } catch (e) {
      toast(messageErreur(e), true)
      setEntree(false)
    }
  }, [entree, selection, toast])

  const statutJournee = d.journees.find((j) => j.date === selection)?.status ?? null
  const enCours = entree ? 'true' : undefined
  // Ce qu'on dit quand on ne sait pas encore : jamais un état conforme.
  const attente = d.erreur ? 'Journée illisible — voir l’erreur ci-dessus' : 'Lecture de la journée…'

  return (
    <div className="wrap">
      <div className="ac-head">
        <h1><small>Accueil · sélection de journée</small>{formatDate(selection)}</h1>
        {/* Les journées et leur état sont lus à l'ouverture. Une journée ouverte
            depuis un autre poste, un import qui vient d'arriver : rien ne le
            montre tant qu'on n'a pas rechargé la page. */}
        <button className="btn gh sm" title="Relire les journées depuis le serveur"
                style={{ marginLeft: 'auto' }}
                onClick={() => void d.recharger()}>⟳ Recharger</button>
        <div className="meta">
          EXTENSION ORIKA · <span style={{ color: 'var(--ink4)' }}>NON BRANCHÉE</span><br />
          IMPORT MANUEL DANS CE BUILD
        </div>
      </div>

      {/* 1 · sélection de journée */}
      <div className="days">
        {jours.map((j) => {
          const r = d.runs[j.date]
          const v = etatVerrou(r ?? null, user?.id ?? null)
          const [lampe, texte] =
            j.status === 'archived' ? ['', 'Archivée · SotCP V3'] as const
            : v.etat === 'occupee' ? ['warn', `Occupée par ${v.par}`] as const
            : r?.statut === 'cloturee' ? ['ok', 'Run clôturée'] as const
            : j.status === 'cloturee' ? ['ok', 'Journée clôturée'] as const
            : r ? ['act', 'Run en cours'] as const
            : j.status === 'active' ? ['act', 'Ouverte · run à créer'] as const
            : ['', 'Aucune journée'] as const
          return (
            <button className="day" key={j.date} aria-pressed={selection === j.date}
                    onClick={() => setSelection(j.date)}>
              <div className="d1">{j.date}</div>
              <div className="d2">{formatMedium(j.date)}</div>
              <div className="d3"><span className={`lamp${lampe ? ` ${lampe}` : ''}`} />{texte}</div>
            </button>
          )
        })}
      </div>

      {/* Une erreur de chargement n'est pas un cul-de-sac : on peut réessayer. */}
      {d.erreur && (
        <Notice c="stop" action={
          <button className="btn sm" onClick={() => void d.recharger()}>Réessayer</button>
        }>{d.erreur}</Notice>
      )}

      <div className="ac-grid">
        <div>
          {/* 2 · état de la run */}
          <Card titre="État de la run"
                lampe={statutJournee === 'archived' ? 'neutre'
                  : !pret ? 'neutre'
                  : run?.statut === 'cloturee' ? 'ok'
                  : verrou.etat === 'occupee' ? 'warn'
                  : run ? 'act' : 'neutre'}
                actions={<span className="chip mo">{selection}</span>}>
            <div className="runstate">
              {statutJournee === 'archived' ? (
                <>
                  <div className="rs-top"><Lamp /><span className="rs-title">Journée archivée</span></div>
                  <p style={{ fontSize: 12.5, color: 'var(--ink3)' }}>
                    L’archivage est la frontière entre les deux applications : cette journée se
                    consulte dans SotCP V3.
                  </p>
                  {/* Même archivée, la journée mène quelque part : son journal reste lisible. */}
                  <div className="rs-actions">
                    <button className="btn" onClick={() => aller({ vue: 'journal', date: selection })}>
                      Ouvrir le journal du {formatMedium(selection)}
                    </button>
                  </div>
                </>
              ) : (
                <>
                  <div className="rs-top">
                    <Lamp c={!pret ? 'neutre'
                      : run?.statut === 'cloturee' ? 'ok'
                      : verrou.etat === 'occupee' ? 'warn' : 'act'}
                          pulse={pret && !!run && run.statut === 'ouverte'} />
                    <span className="rs-title">
                      {!pret ? attente
                        : !run ? 'Run non commencée'
                        : run.statut === 'cloturee' ? 'Run clôturée' : 'Run en cours'}
                    </span>
                    {pret && courante && run?.statut === 'ouverte' && (
                      <Chip c="act">ÉTAPE {courante.code} · {courante.libelle.toUpperCase()}</Chip>
                    )}
                    {pret && etapesSel.length > 0 && <Chip>{p.validees}/{etapesSel.length} VALIDÉES</Chip>}
                    {pret && run && run.duree_cumulee_s > 0 && <Chip>{duree(run.duree_cumulee_s)}</Chip>}
                  </div>

                  {pret && etapesSel.length > 0 && (
                    <>
                      <div className="rs-bar">
                        {etapesSel.map((e) => (
                          <i key={e.code} className={
                            e.statut === 'validee' ? 'done'
                              : e.statut === 'skip_regle' || e.statut === 'skip_question' ? 'skip'
                              : e.statut === 'de_cote' ? 'park'
                              : e.code === courante?.code ? 'cur' : ''} />
                        ))}
                      </div>
                      <div className="rs-legend">
                        <span><i className="lamp ok" />VALIDÉE</span>
                        <span><i className="lamp act" />EN COURS</span>
                        <span><i className="lamp warn" />DE CÔTÉ</span>
                        <span><i className="lamp" />À FAIRE</span>
                      </div>
                    </>
                  )}

                  {verrou.etat === 'occupee' && (
                    <div className="locked">
                      <Lamp c="warn" />
                      <div>
                        <b>{verrou.par}</b> est dans cette run depuis {heure(verrou.depuis)} —
                        une seule personne à la fois.<br />
                        <span className="mo" style={{ fontSize: 10, color: 'var(--ink3)' }}>
                          LE VERROU EXPIRE SEUL APRÈS 15 MIN SANS ACTIVITÉ
                        </span>
                      </div>
                    </div>
                  )}

                  {run?.cloturee_par_nom && (
                    <div className="li" style={{ marginTop: 12, paddingLeft: 0 }}>
                      <span className="av">{initiales(run.cloturee_par_nom)}</span>
                      <div className="body">
                        <div className="t">{run.cloturee_par_nom} a clôturé la journée</div>
                        <div className="s">À {heure(run.cloturee_at)}</div>
                      </div>
                    </div>
                  )}

                  <div className="rs-actions">
                    {verrou.etat === 'occupee' ? (
                      <>
                        {/* Déloger un collègue est destructif : ton `stop`, et on
                            demande confirmation avant de le faire. */}
                        <button className="btn stop lg" data-encours={enCours}
                                onClick={() => setDemandeMain(true)}>
                          Prendre la main sur {verrou.par}
                        </button>
                        <span className="hint">
                          Entrer sans déloger n’est pas possible : la run tient un seul poste.
                        </span>
                      </>
                    ) : run?.statut === 'cloturee' ? (
                      <button className="btn pri lg" onClick={() => aller({ vue: 'bilan', date: selection })}>
                        Voir le bilan du {formatMedium(selection)}
                      </button>
                    ) : (
                      <button className="btn pri lg" disabled={!pret} data-encours={enCours}
                              onClick={() => void entrer(false)}>
                        {!pret ? attente
                          : courante ? `Reprendre à l’étape ${courante.code} · ${courante.libelle}`
                          : run ? 'Reprendre — solder la journée'
                          : 'Commencer la journée'}
                      </button>
                    )}
                    <button className="btn gh sm" onClick={() => aller({ vue: 'journal', date: selection })}>
                      Journal
                    </button>
                    <button className="btn gh sm" onClick={() => aller({ vue: 'remise', date: selection })}>
                      Remise
                    </button>
                    <button className="btn gh sm" onClick={() => void d.recharger()}>
                      Rafraîchir
                    </button>
                    <span className="mo" style={{ fontSize: 10, color: 'var(--ink4)' }}>
                      PROGRESSION SAUVEGARDÉE EN CONTINU
                    </span>
                  </div>
                </>
              )}
            </div>
          </Card>

          {/* 4 · ce qui t'attend aujourd'hui */}
          <Card titre="Ce qui t’attend aujourd’hui" lampe={pret ? 'act' : 'neutre'} style={{ marginTop: 14 }}>
            {!pret
              ? <Vide>{attente}</Vide>
              : attendu.length === 0
                ? <Vide>Journée ordinaire — rien de particulier au calendrier.</Vide>
                : attendu.map((a) => (
                  <div className="li" key={a.cle}>
                    <Lamp c="act" />
                    <div className="body"><div className="t">{a.texte}</div></div>
                  </div>
                ))}
          </Card>

          {/* 5 · coup d'œil sur hier — QUE DES ÉCARTS DANS LA MOITIÉ DROITE */}
          <Card titre={`Coup d’œil sur ${d.datePrec ? formatMedium(d.datePrec) : 'hier'}`}
                lampe={pret && hier ? hier.lampe : 'neutre'}
                style={{ marginTop: 14 }}>
            {!pret
              ? <Vide>{attente}</Vide>
              : !d.datePrec || !hier
                ? <Vide>Aucune journée travaillée avant celle-ci.</Vide>
                : <Kpis items={hier.items} />}
          </Card>
        </div>

        <div>
          {/* 3 · reste d'hier */}
          <Card titre="Reste d’hier" lampe={graviteReste} style={{ marginBottom: 14 }}
                actions={!pret ? <Chip>…</Chip>
                  : reste.length ? <Chip c={graviteReste}>{reste.length}</Chip>
                  : <Chip c="ok">RIEN</Chip>}>
            {!pret
              ? <Vide>{attente}</Vide>
              : reste.length === 0
                ? <Vide>Rien ne traîne — journée soldée.</Vide>
                : <>
                  {reste.slice(0, 12).map((r) => (
                    <div className="li" key={r.cle}>
                      <Lamp c={r.gravite} />
                      <div className="body">
                        <div className="t">{r.titre}</div>
                        <div className="s">{r.detail}</div>
                        {r.etape && (
                          // Un lien qui mène ailleurs est un bouton : on le voit,
                          // on le vise, il dit où il va.
                          <button className="btn sm" style={{ marginTop: 6 }}
                                  onClick={() => aller({ vue: 'run', date: r.date, etape: r.etape ?? null })}>
                            Ouvrir l’étape {r.etape} du {formatMedium(r.date)} →
                          </button>
                        )}
                      </div>
                    </div>
                  ))}
                  {reste.length > 12 && (
                    // Douze lignes affichées, le reste ne doit pas disparaître en silence.
                    <div className="li">
                      <Lamp c="warn" />
                      <div className="body">
                        <div className="t">
                          {pluriel(reste.length - 12, 'autre point non affiché', 'autres points non affichés')}
                        </div>
                        <div className="s">LES PLUS GRAVES SONT EN TÊTE DE LISTE</div>
                      </div>
                    </div>
                  )}
                </>}
            <div className="hint" style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
              Informatif uniquement — aucune action ne se fait depuis l’accueil.
              L’écart télécollecte / dossier AIM et les signalements caissiers rejoindront ce bloc
              quand ils existeront en base.
            </div>
          </Card>

          {/* 6 · communication */}
          <Card titre="Communication" lampe={d.messages.length ? 'act' : 'neutre'}>
            {d.messages.length === 0
              ? <Vide>Aucun message.</Vide>
              : d.messages.slice(-4).map((m) => (
                <div className="msg" key={m.id}>
                  <div className="who">
                    <span className={`av${m.auteur === user?.id ? ' b' : ''}`}>{initiales(m.auteur_nom)}</span>
                    <span className="nm">{m.auteur_nom ?? '—'}</span>
                    <span className="tm">{timeAgo(m.at)}</span>
                  </div>
                  <div className="tx">{m.texte}</div>
                </div>
              ))}
            <div className="hint" style={{ padding: '8px 12px', borderTop: '1px solid var(--line)' }}>
              Le fil complet et les consignes vivent dans le panneau latéral, en bas à droite.
            </div>
          </Card>
        </div>
      </div>

      {demandeMain && verrou.etat === 'occupee' && (
        <Modale titre="Prendre la main sur la run ?"
                sous={`${verrou.par} y travaille depuis ${heure(verrou.depuis)}. `
                  + 'Son poste perdra le verrou sans avertissement, et la prise de main '
                  + 'est inscrite au journal de la journée.'}
                fermer={() => setDemandeMain(false)}>
          <div className="btns">
            <button className="btn" onClick={() => setDemandeMain(false)}>Annuler</button>
            <button className="btn stop" data-encours={enCours} onClick={() => void entrer(true)}>
              Prendre la main
            </button>
          </div>
        </Modale>
      )}
    </div>
  )
}

// ── Le coup d'œil sur hier ─────────────────────────────────────────────────
//
// `coupDoeil()` de briefing.ts rend des CHAÎNES déjà formatées par fmtEur : à
// l'arrivée on ne sait plus si « 12,40 € » est un montant ou un écart, et un
// excédent s'écrit comme un montant ordinaire. On refait donc les quatre
// chiffres ici, à partir des mêmes sources, pour garder les nombres.
//
// Et surtout : la SOMME SIGNÉE des écarts caisses est un mensonge. Une caisse à
// +50 € et une caisse à −50 € donnent zéro, donc « conforme », donc vert — alors
// que la journée porte deux anomalies de 50 €. Ce qui se lit d'un coup d'œil,
// c'est la MASSE des écarts (somme des valeurs absolues), le nombre de caisses
// concernées, et le pire écart, qui donne la couleur.
function bilanHier(rapport: Rapport | null): {
  items: Array<{ k: string; v: string; s?: string; couleur?: string }>
  lampe: Couleur
} | null {
  if (!rapport) return null

  const c = etatCoffre(rapport)
  const ops = Array.isArray(rapport.rapportOps)
    ? (rapport.rapportOps as Array<Record<string, unknown>>) : []

  const declare = ops.reduce((s, o) => s + (Number(o.decl) || 0), 0)
  const masse = ops.reduce((s, o) => s + Math.abs(Number(o.diff) || 0), 0)
  const enEcart = ops.filter((o) => severite(Number(o.diff) || 0) !== 'nul').length
  const pire = ops.reduce((m, o) => {
    const v = Number(o.diff) || 0
    return Math.abs(v) > Math.abs(m) ? v : m
  }, 0)

  // Le vert ne dit qu'UNE chose : il n'y a pas d'écart. Or la masse peut être
  // non nulle alors que chaque caisse, prise seule, reste sous l'epsilon — dix
  // caisses à un demi-centime font quand même de l'argent déplacé. Colorer cette
  // masse-là en vert, c'est écrire « conforme » sous un montant non nul : elle
  // passe en neutre, jamais en vert.
  const cumulSeul = masse >= EPSILON && severite(pire) === 'nul'

  // Sans données, pas de couleur : « 0 sur 0 » n'est pas une journée conforme.
  const items: Array<{ k: string; v: string; s?: string; couleur?: string }> = [
    {
      k: 'Coffre laissé',
      v: c.existe ? fmtEur(c.totalCompte) : '—',
      ...(c.existe ? {} : { s: 'AUCUN INVENTAIRE' }),
    },
    {
      k: 'Écart de coffre',
      v: c.existe ? fmtEcart(c.ecart) : '—',
      ...(c.existe ? { couleur: couleurEcart(c.ecart), s: motEcart(c.ecart).toUpperCase() } : {}),
    },
    {
      k: 'Déclaré caisses',
      v: ops.length ? fmtEur(declare) : '—',
      ...(ops.length ? { s: `${ops.length} CAISSE${ops.length > 1 ? 'S' : ''}` } : {}),
    },
    {
      k: 'Masse des écarts',
      v: ops.length ? fmtEur(masse) : '—',
      ...(ops.length
        ? {
          couleur: cumulSeul ? 'var(--ink3)' : couleurEcart(pire),
          s: enEcart === 0
            ? (cumulSeul ? 'CUMUL D’ARRONDIS SOUS LE CENTIME' : 'AUCUNE CAISSE EN ÉCART')
            : `${enEcart} CAISSE${enEcart > 1 ? 'S' : ''} · PIRE ${fmtEcart(pire)}`,
        }
        : {}),
    },
  ]

  // La lampe du bloc dit le plus grave des deux écarts, coffre et caisses —
  // via l'échelle commune, pas une copie locale des seuils.
  const grave = Math.max(c.existe ? Math.abs(c.ecart) : 0, ops.length ? Math.abs(pire) : 0)
  const lampe: Couleur = !c.existe && ops.length === 0 ? 'neutre'
    : cumulSeul && severite(grave) === 'nul' ? 'neutre'
    : lampeEcart(grave)

  return { items, lampe }
}
